/**
 * Header Component
 *
 * Site header with Prismic-controlled navigation.
 * Includes mobile menu, sticky behavior, and color scheme adaptation.
 */

import React from "react";
import { graphql, Link } from "gatsby";
import FancyLink from "../FancyLink";
import Button from "../Button";
import Icon from "../Icon";
import VisuallyHidden from "../VisuallyHidden";
import * as styles from "./Header.module.scss";

const Header = ({ topMenu }) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = React.useState(false);
  const [isSticky, setIsSticky] = React.useState(false);

  const { logo, logo_alt_text, menu_links, button_label, button_link } = topMenu || {};

  const closeMobileNav = () => setIsMobileNavOpen(false);

  // Toggle mobile nav
  function handleNavToggleClick(e) {
    e.preventDefault();
    setIsMobileNavOpen(prevState => !prevState);
  }

  // Close when any link inside the mobile nav is clicked (anchors, pages, CTA)
  function handleMobileNavClick(e) {
    if (e.target.closest("a")) {
      closeMobileNav();
    }
  }

  // Lock body scroll when mobile nav is open
  React.useEffect(() => {
    if (isMobileNavOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => document.body.classList.remove("overflow-hidden");
  }, [isMobileNavOpen]);

  // Track scroll for sticky header
  React.useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile nav on route change
  React.useEffect(() => {
    setIsMobileNavOpen(false);
  }, []);

  const menuLinks = menu_links?.map((menuLink, index) => (
    <li key={`nav-link-${index}`}>
      <FancyLink
        href={menuLink.link?.url || "/"}
        target={menuLink.link?.target}
      >
        {menuLink.label}
      </FancyLink>
    </li>
  ));

  return (
    <header
      className={styles.header}
      data-menu-open={isMobileNavOpen}
      data-sticky={isSticky}
    >
      <div className={`page-width ${styles.container}`}>
        {/* Logo */}
        <Link to="/" className={styles.logo} aria-label={logo_alt_text || "Home"}>
          {logo?.url ? (
            <img
              src={logo.url}
              alt={logo_alt_text || "Logo"}
              className={styles.logoImage}
            />
          ) : (
            <span className={styles.logoText}>{logo_alt_text || "Low Battery"}</span>
          )}
        </Link>

        {/* Desktop Navigation */}
        {menuLinks && menuLinks.length > 0 && (
          <nav className={styles.nav} id="nav" aria-label="Main navigation">
            <ul className={styles.navList}>{menuLinks}</ul>
          </nav>
        )}

        {/* CTA Button */}
        {button_label && (
          <Button
            href={button_link?.url || "/#contact"}
            target={button_link?.target}
            variant="solid"
            className={styles.navBtn}
          >
            {button_label}
          </Button>
        )}

        {/* Mobile Menu Toggle */}
        <button
          className={styles.navToggle}
          aria-expanded={isMobileNavOpen}
          aria-controls="mobile-nav"
          onClick={handleNavToggleClick}
        >
          <Icon name={isMobileNavOpen ? "close" : "menu"} size="24px" />
          <VisuallyHidden>
            {(isMobileNavOpen ? "Close" : "Open") + " navigation"}
          </VisuallyHidden>
        </button>
      </div>

      {/* Mobile Navigation */}
      <div
        className={styles.mobileNav}
        id="mobile-nav"
        aria-hidden={!isMobileNavOpen}
      >
        <nav aria-label="Mobile navigation" onClick={handleMobileNavClick}>
          {menuLinks && <ul>{menuLinks}</ul>}
          {button_label && (
            <Button
              href={button_link?.url || "/#contact"}
              target={button_link?.target}
              variant="solid"
              className={styles.mobileNavBtn}
            >
              {button_label}
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;

export const query = graphql`
  fragment TopMenuFragment on PrismicTopMenu {
    _previewable
    data {
      logo {
        url
        alt
      }
      logo_alt_text
      menu_links {
        label
        link {
          url
          target
        }
      }
      button_label
      button_link {
        url
        target
      }
    }
  }
`;
